/**
 * Single source of truth for /compare-screens-v2/choose and /compare-screens-v2/compare.
 * Not wired to Supabase (local config only).
 */

export const COMPARE_V2_MODELS = [
  {
    id: 'echomap-73sv-uhd2',
    shortLabel: 'ECHOMAP UHD2 73sv',
    screenSizeInches: 7,
    screenSizeLabel: '7"',
    shortDescription: 'Compact 7" ECHOMAP for tight consoles and budget-first installs.',
    priceTierLabel: 'Budget',
    buyerArchetype: 'budget',
    startingPrice: 'Starting around $900',
    bestForYouIf:
      'You want a real chartplotter in the smallest footprint and don’t need flagship networking yet.',
    whyYouPickIt:
      'It’s the easiest way to step up from a phone or handheld without crowding the dash.',
    overallScore: 6,
    priceRank: 899.99,
  },
  {
    id: 'echomap-93sv-uhd2',
    shortLabel: 'ECHOMAP UHD2 93sv',
    screenSizeInches: 9,
    screenSizeLabel: '9"',
    shortDescription: 'Popular 9" ECHOMAP, big enough to split views, still simple to run.',
    priceTierLabel: 'Mid',
    buyerArchetype: 'balanced',
    startingPrice: 'Starting around $1,200',
    bestForYouIf:
      'You split screens often and want a 9" display before jumping to GPSMAP pricing.',
    whyYouPickIt:
      'It’s the “just right” size for most boats: readable, still ECHOMAP-simple to run.',
    overallScore: 8,
    priceRank: 1199.99,
  },
  {
    id: 'gpsmap-943xsv',
    shortLabel: 'GPSMAP 943xsv',
    screenSizeInches: 9,
    screenSizeLabel: '9"',
    shortDescription: '9" GPSMAP for networking, durability, and pro-boat builds.',
    priceTierLabel: 'Mid',
    buyerArchetype: 'balanced',
    startingPrice: 'Starting around $1,600',
    bestForYouIf:
      'You’re investing in Garmin networking and want GPSMAP toughness in a 9" package.',
    whyYouPickIt:
      'You care about long-term expandability and a pro feel without a monster helm.',
    overallScore: 7,
    priceRank: 1599.99,
  },
  {
    id: 'gpsmap-1042xsv',
    shortLabel: 'GPSMAP 1042xsv',
    screenSizeInches: 10,
    screenSizeLabel: '10"',
    shortDescription: '10" keypad GPSMAP for bright sun, gloves, and confident control.',
    priceTierLabel: 'Premium',
    buyerArchetype: 'premium',
    startingPrice: 'Starting around $1,700',
    bestForYouIf:
      'You prefer keypad control and a bright 10" screen for sun, spray, and gloves.',
    whyYouPickIt:
      'You want fewer fingerprints and confident control when the boat is moving.',
    overallScore: 7,
    priceRank: 1699.99,
  },
  {
    id: 'gpsmap-1243xsv',
    shortLabel: 'GPSMAP 1243xsv',
    screenSizeInches: 12,
    screenSizeLabel: '12"',
    shortDescription: 'Flagship 12" GPSMAP for glass helms and maximum screen real estate.',
    priceTierLabel: 'Premium',
    buyerArchetype: 'premium',
    startingPrice: 'Starting around $3,100',
    bestForYouIf:
      'You’re building a premium helm and want maximum screen for maps, sonar, and splits.',
    whyYouPickIt:
      'You’re optimizing for visibility and room to grow. This is the statement piece.',
    overallScore: 9,
    priceRank: 3099.99,
  },
];

/** compare_id allowlist for session + `?recommended=` validation (choose + compare routes). */
export const COMPARE_V2_COMPARE_IDS = COMPARE_V2_MODELS.map((m) => m.id);

/** Quick lookup by id */
export const COMPARE_V2_BY_ID = Object.fromEntries(
  COMPARE_V2_MODELS.map((m) => [m.id, m]),
);

export function getCompareV2Model(id) {
  return COMPARE_V2_BY_ID[id] ?? null;
}

/** Query param on /compare-screens-v2/compare for choose → compare handoff */
export const COMPARE_V2_RECOMMENDED_QUERY = 'recommended';

/**
 * Validates recommended id from a location search string (e.g. `?recommended=…`).
 */
export function getRecommendedIdFromLocationSearch(search) {
  const s = search ?? '';
  const raw = new URLSearchParams(s.startsWith('?') ? s : `?${s}`).get(
    COMPARE_V2_RECOMMENDED_QUERY,
  );
  return raw && getCompareV2Model(raw) ? raw : null;
}

/**
 * Up to three quick-add rows after landing with a recommended model.
 * `subtext` is one short line (cheaper / bigger / lineup), not live pricing.
 */
export function getHandoffCompareSuggestions(recommendedId) {
  const base = getCompareV2Model(recommendedId);
  if (!base) return [];

  const out = [];
  const push = (id, subtext) => {
    if (!id || id === recommendedId) return;
    if (out.some((s) => s.id === id)) return;
    const m = getCompareV2Model(id);
    if (!m) return;
    out.push({
      id,
      shortLabel: m.shortLabel,
      subtext,
    });
  };

  switch (recommendedId) {
    case 'echomap-73sv-uhd2':
      push('echomap-93sv-uhd2', 'Larger 9" screen');
      push('gpsmap-943xsv', '9" GPSMAP option');
      break;
    case 'echomap-93sv-uhd2':
      push('echomap-73sv-uhd2', 'Cheaper 7" option');
      push('gpsmap-943xsv', '9" GPSMAP');
      push('gpsmap-1042xsv', 'Bigger 10" upgrade');
      break;
    case 'gpsmap-943xsv':
      push('echomap-93sv-uhd2', 'Cheaper 9" ECHOMAP');
      push('gpsmap-1042xsv', '10" step up');
      push('gpsmap-1243xsv', '12" flagship');
      break;
    case 'gpsmap-1042xsv':
      push('gpsmap-943xsv', 'Smaller 9" GPSMAP');
      push('echomap-93sv-uhd2', 'Popular 9" value');
      push('gpsmap-1243xsv', '12" flagship');
      break;
    case 'gpsmap-1243xsv':
      push('gpsmap-1042xsv', '10" premium');
      push('gpsmap-943xsv', '9" GPSMAP');
      push('echomap-93sv-uhd2', 'Common 9" build');
      break;
    default:
      break;
  }

  return out.slice(0, 3);
}

/**
 * Normalizes choose-flow size state so '10', 10, or stray whitespace still map to bucket keys.
 */
export function normalizeChooseSizeKey(size) {
  const t = String(size ?? '').trim();
  if (t === '7' || t === '9' || t === '10' || t === '12') return t;
  const n = Number(t);
  if (n === 7) return '7';
  if (n === 9) return '9';
  if (n === 10) return '10';
  if (n === 12) return '12';
  return '';
}

export function modelMatchesChooseSizeAnswer(model, normalizedSizeKey) {
  if (!model || !normalizedSizeKey) return false;
  if (normalizedSizeKey === '12') return model.screenSizeInches >= 12;
  const want = { '7': 7, '9': 9, '10': 10 }[normalizedSizeKey];
  return want != null && model.screenSizeInches === want;
}

/**
 * Model ids allowed for a size answer. Enforces sane recommendations (e.g. 10" choice never yields 7").
 * sizeKey: '7' | '9' | '10' | '12' (matches choose UI)
 */
export function getAllowedModelIdsForSizeAnswer(sizeKey) {
  const key = normalizeChooseSizeKey(sizeKey);
  if (key === '7') {
    return COMPARE_V2_MODELS.filter((m) => m.screenSizeInches === 7).map((m) => m.id);
  }
  if (key === '9') {
    return COMPARE_V2_MODELS.filter((m) => m.screenSizeInches === 9).map((m) => m.id);
  }
  if (key === '10') {
    return COMPARE_V2_MODELS.filter((m) => m.screenSizeInches === 10).map((m) => m.id);
  }
  if (key === '12') {
    return COMPARE_V2_MODELS.filter((m) => m.screenSizeInches >= 12).map((m) => m.id);
  }
  return [];
}

/** Strip blurbs under Best Overall / Value / Premium (compare page only). */
export const COMPARE_V2_STRIP_REASONS = {
  overall: {
    'echomap-73sv-uhd2': 'Best fit when you want capability without paying for inches you don’t need.',
    'echomap-93sv-uhd2': 'The balanced pick most anglers land on when they’re torn on size.',
    'gpsmap-943xsv': 'Strong when networking and build quality matter as much as screen size.',
    'gpsmap-1042xsv': 'Great if you want GPSMAP control and a big view without going full 12".',
    'gpsmap-1243xsv': 'Top choice when your selection skews flagship: big water and big dashboards.',
  },
  value: {
    'echomap-73sv-uhd2': 'Keeps the most money in your pocket for transducers and accessories.',
    'echomap-93sv-uhd2': 'Still reasonable for a large ECHOMAP if you stretch past entry-level.',
    'gpsmap-943xsv': 'Mid-tier GPSMAP value: features first, dollars second.',
    'gpsmap-1042xsv': 'More real estate per dollar when keypad control matters to you.',
    'gpsmap-1243xsv': 'Premium spend, worth it when only the largest screen will do.',
  },
  premium: {
    'echomap-73sv-uhd2': 'Entry-level in your set. Premium usually means stepping up in size.',
    'echomap-93sv-uhd2': 'Premium for ECHOMAP; still not the top of a GPSMAP-heavy lineup.',
    'gpsmap-943xsv': 'Serious GPSMAP in a compact space: premium without the biggest panel.',
    'gpsmap-1042xsv': 'Keypad GPSMAP with a commanding 10" view.',
    'gpsmap-1243xsv': 'The no-compromise helm screen when you want the biggest and best.',
  },
};
